import { useState, useEffect } from 'react';
import { syncUsersFromRemote, SITE_ID } from '../../../utils/sharedStorage';

interface AdminUser {
  phone: string;
  name: string;
  country: string;
}

const SUPER_ADMINS = ['97800237', '92252664'];

export default function AdminManagement() {
  const [adminList, setAdminList] = useState<string[]>([]);
  const [allUsers, setAllUsers] = useState<AdminUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmRevoke, setConfirmRevoke] = useState<{ phone: string; name: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // ✅ Charger la liste des admins
      const raw = localStorage.getItem('adminList') || '[]';
      let storedAdmins: string[] = [];
      try { storedAdmins = JSON.parse(raw); } catch { storedAdmins = []; }
      if (!Array.isArray(storedAdmins)) storedAdmins = [];
      const mergedAdmins = Array.from(new Set([...SUPER_ADMINS, ...storedAdmins]));
      setAdminList(mergedAdmins);

      // ✅ Vérifier si l'admin connecté est super admin
      try {
        const sessionRaw = sessionStorage.getItem('adminSession');
        if (sessionRaw) {
          const session = JSON.parse(sessionRaw);
          setIsSuperAdmin(SUPER_ADMINS.includes(session.phone || ''));
        }
      } catch { setIsSuperAdmin(false); }

      // ✅ Charger tous les utilisateurs avec le bon préfixe
      const users = await syncUsersFromRemote();
      setAllUsers(users.map(u => ({ phone: u.phone, name: u.name, country: u.country || '' })));
    } catch (e) {
      console.error('Erreur chargement AdminManagement:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = (userPhone: string) => {
    if (!adminList.includes(userPhone)) {
      const updatedAdmins = [...adminList, userPhone];
      const toStore = updatedAdmins.filter(p => !SUPER_ADMINS.includes(p));
      localStorage.setItem('adminList', JSON.stringify(toStore));
      setAdminList(updatedAdmins);
    }
  };

  const handleRemoveAdmin = (userPhone: string) => {
    if (SUPER_ADMINS.includes(userPhone)) return;
    const updatedAdmins = adminList.filter(phone => phone !== userPhone);
    const toStore = updatedAdmins.filter(p => !SUPER_ADMINS.includes(p));
    localStorage.setItem('adminList', JSON.stringify(toStore));
    setAdminList(updatedAdmins);
    setConfirmRevoke(null);
  };

  const filteredUsers = allUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone.includes(searchTerm) ||
    user.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const nonAdminUsers = filteredUsers.filter(user => !adminList.includes(user.phone));
  const adminUsers = allUsers.filter(user => adminList.includes(user.phone));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <i className="ri-loader-4-line text-3xl text-teal-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div>
      {/* Modale confirmation révocation */}
      {confirmRevoke && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-shield-cross-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Révoquer l'administrateur</h3>
                <p className="text-sm text-gray-500">Cette action est réversible</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-800">
                Révoquer les droits admin de <strong>{confirmRevoke.name || confirmRevoke.phone}</strong> ?
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmRevoke(null)}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={() => handleRemoveAdmin(confirmRevoke.phone)}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-shield-cross-line"></i>
                Révoquer
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Gestion des administrateurs</h2>
        <p className="text-sm text-gray-600">
          {isSuperAdmin
            ? 'Vous êtes super-administrateur. Vous pouvez nommer ou révoquer des administrateurs.'
            : 'Vous êtes administrateur.'}
        </p>
      </div>

      {/* Liste des admins actuels */}
      <div className="mb-8">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <i className="ri-shield-star-line text-teal-600"></i>
          Administrateurs actuels ({adminUsers.length})
        </h3>
        <div className="space-y-3">
          {adminUsers.length === 0 && (
            <p className="text-gray-400 text-sm py-4 text-center">Aucun administrateur trouvé</p>
          )}
          {adminUsers.map(user => {
            const isSuperAdminUser = SUPER_ADMINS.includes(user.phone);
            return (
              <div key={user.phone} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl bg-gray-50 hover:bg-white transition">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isSuperAdminUser ? 'bg-gradient-to-br from-amber-400 to-orange-500' : 'bg-teal-600'}`}>
                    <i className="ri-shield-star-line text-white text-lg"></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold text-gray-900">{user.name || user.phone}</h4>
                      {isSuperAdminUser && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold rounded-full border border-amber-300">
                          ⭐ Super Admin
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{user.phone}{user.country ? ` · ${user.country}` : ''}</p>
                  </div>
                </div>
                {isSuperAdmin && !isSuperAdminUser && (
                  <button
                    onClick={() => setConfirmRevoke({ phone: user.phone, name: user.name })}
                    className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                  >
                    <i className="ri-shield-cross-line"></i>
                    Révoquer
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Ajouter un nouvel admin (seulement pour super-admins) */}
      {isSuperAdmin && (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-user-add-line text-teal-600"></i>
            Nommer un nouvel administrateur
          </h3>

          <div className="mb-4 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
            <input
              type="text"
              placeholder="Rechercher un utilisateur par nom, téléphone ou pays..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-circle-fill text-lg"></i>
              </button>
            )}
          </div>

          {searchTerm && (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {nonAdminUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <i className="ri-user-search-line text-4xl mb-2 block"></i>
                  <p className="text-sm">Aucun utilisateur trouvé</p>
                </div>
              ) : (
                nonAdminUsers.map(user => (
                  <div key={user.phone} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-600 font-bold text-sm">{(user.name || '?').charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm">{user.name}</h4>
                        <p className="text-xs text-gray-500">{user.phone}{user.country ? ` · ${user.country}` : ''}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddAdmin(user.phone)}
                      className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-1.5 shadow"
                    >
                      <i className="ri-shield-star-line"></i>
                      Nommer admin
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
